import {
  ClassMonthlyReportService,
  StatisticService,
  InitEnvironmentVariable,
  ClassStatisticsService
} from '@PikaDoApp/npm-pikado-logic';
import {
  ClassMonthlyReportExtended,
  ClassMonthlyReportBySchoolAndClass,
} from './ClassMonthlyReportTypes';
import {store} from '../../../redux/store';
import {storeClassReport, storeClassMonthlyReportByMonth} from '../../../redux/actions/fullReport';
import {getEnvironment} from '../../../lib/getEnvironment';
import { ClassStatistics, ClassMonthlyReport } from '@PikaDoApp/npm-pikado-logic';
import moment from 'moment';
/**
 * Service wrapper for class monthly reports from npm-pikado-logic
 * This service fetches class-level report data with caching
 * Note: Uses the same Amplify configuration initialized by NewStudentReportService
 */
export class ClassMonthlyReportServiceWrapper {
  private classMonthlyReportService: ClassMonthlyReportService;
  private classStatisticsService: ClassStatisticsService;
  private readonly CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours (same as student reports)

  constructor() {
    InitEnvironmentVariable(getEnvironment());
    this.classMonthlyReportService = new ClassMonthlyReportService();
    this.classStatisticsService = new ClassStatisticsService();
  }

  /**
   * Get class monthly reports by teacher and class
   * Implements 2-hour caching strategy
   * @param classId - Class ID
   * @param teacherId - Teacher ID
   * @returns Array of ClassMonthlyReportExtended or null
   */

  async getReportsByTeacherAndClass(
    classId: string,
    teacherId: string,
  ): Promise<ClassMonthlyReportExtended[] | null> {
    const cacheKey = `${classId}-${teacherId}`;

    // Check for cached report in Redux state
    const state = store.getState();
    const cached = state.fullReport?.reportsByClassId?.[cacheKey];

    if (cached) {
      const now = Date.now();

      if (cached.timestamp && now - cached.timestamp <= this.CACHE_DURATION) {
        const lastUpdated = new Date(cached.timestamp).toLocaleString();
        const expiresAt = new Date(
          cached.timestamp + this.CACHE_DURATION,
        ).toLocaleString();

        console.log(
          `✅ Returning cached class report for: ${cacheKey}\n` +
            `Last updated: ${lastUpdated}\n` +
            `Next update at: ${expiresAt}`,
        );

        return cached.data;
      }

      // Cache is stale
      console.log('⏰ Cached class report stale for:', cacheKey);
    }

    // Cache miss or expired: fetch fresh data
    try {
      console.log(
        '🔄 Fetching fresh class report (by teacher) for:',
        cacheKey,
      );
      const startTime = performance.now();

      const reports = (await this.classMonthlyReportService.getReportsByTeacherAndClass(
        classId,
        teacherId,
      )) as any[];

      // Debug: Log raw API response AS OBJECT (expandable in console)
      console.log('[CLASS REPORT - API RESPONSE] 📥 Raw data from backend (by teacher) - FULL OBJECT:', reports);

      // Check if dailyPerformances exists
      if (reports && reports.length > 0 && reports[0].monthlyReports && reports[0].monthlyReports.length > 0) {
        const firstStudent = reports[0].monthlyReports[0];
        console.log('[CLASS REPORT - API RESPONSE] 🔍 First student monthlyReportId:', firstStudent.monthlyReportId);
        console.log('[CLASS REPORT - API RESPONSE] 🔍 dailyPerformances exists?', !!firstStudent.monthlyReportId?.dailyPerformances);
        console.log('[CLASS REPORT - API RESPONSE] 🔍 dailyPerformances length:', firstStudent.monthlyReportId?.dailyPerformances?.length || 0);

        if (firstStudent.monthlyReportId?.dailyPerformances && firstStudent.monthlyReportId.dailyPerformances.length > 0) {
          const firstDay = firstStudent.monthlyReportId.dailyPerformances[0];
          console.log('[CLASS REPORT - API RESPONSE] 🔍 First day data:', firstDay);
          console.log('[CLASS REPORT - API RESPONSE] 🔍 First performanceId:', firstDay.performanceId);
        }
      }

      // Log numOfTotalAns from backend for each student and day
      reports?.forEach((report, idx) => {
        console.log(`[CLASS REPORT - API RESPONSE] 🎓 Report ${idx + 1} - Academic Year: ${report.academicYear}`);
        report.monthlyReports?.forEach((monthlyReport) => {
          const studentId = monthlyReport.monthlyReportId?.studentId;
          const month = monthlyReport.monthlyReportId?.month;
          console.log(`  👤 Student: ${studentId}, Month: ${month}`);
          monthlyReport.monthlyReportId?.dailyPerformances?.forEach((dailyPerf) => {
            console.log(`    📆 Day ${dailyPerf.day}: numOfTotalAns = ${dailyPerf.performanceId?.numOfTotalAns || 0}`);
          });
        });
      });

      const endTime = performance.now();
      console.log(`⚡ Class report fetch took ${endTime - startTime}ms`);

      // Cache the fetched data with timestamp
      const cacheEntry = {data: reports, timestamp: Date.now()};
      store.dispatch(storeClassReport(cacheKey, cacheEntry));

      return reports;
    } catch (error) {
      console.error('❌ Error in getReportsByTeacherAndClass:', error);
      throw error;
    }
  }


  async getClassStatisticsByClassId(
    classId: string,
  ): Promise<ClassStatistics> {


    // Cache miss or expired: fetch fresh data
    try {
      const response = await this.classStatisticsService.getClassStatisticsByClassId(
        classId
      ) as any;


      // Extract the actual statistics from the nested response structure
      const statistics = response?.data?.classStatisticsByClassId || response;

      return statistics;
    } catch (error) {
      console.error('❌ Error in getClassStatisticsByClassId:', error);
      throw error;
    }
  }

//Deprecated
  /**
   * Get class monthly reports by school and class
   * Implements 2-hour caching strategy
   * @param classId - Class ID
   * @param schoolId - School ID
   * @returns Array of ClassMonthlyReportBySchoolAndClass or null
   */
  async getReportsBySchoolAndClass(
    classId: string,
    schoolId: string,
  ): Promise<ClassMonthlyReportBySchoolAndClass[] | null> {
    const cacheKey = `${classId}-${schoolId}`;
    // Check for cached report in Redux state
    const state = store.getState();
    const cached = state.fullReport?.reportsByClassId?.[cacheKey];

    if (cached) {
      const now = Date.now();

      if (cached.timestamp && now - cached.timestamp <= this.CACHE_DURATION) {
        const lastUpdated = new Date(cached.timestamp).toLocaleString();
        const expiresAt = new Date(
          cached.timestamp + this.CACHE_DURATION,
        ).toLocaleString();

        console.log(
          `✅ Returning cached class report for: ${cacheKey}\n` +
            `Last updated: ${lastUpdated}\n` +
            `Next update at: ${expiresAt}`,
        );

        return cached.data;
      }

      // Cache is stale
      console.log('⏰ Cached class report stale for:', cacheKey);
    }

    // Cache miss or expired: fetch fresh data
    try {
      console.log('🔄 Fetching fresh class report (by school) for:', cacheKey);
      const startTime = performance.now();

      const reports = (await this.classMonthlyReportService.getReportsBySchoolAndClass(
        classId,
        schoolId,
      )) as any[];
      // Check if dailyPerformances exists
      if (reports && reports.length > 0 && reports[0].monthlyReports && reports[0].monthlyReports.length > 0) {
        const firstStudent = reports[0].monthlyReports[0];

        if (firstStudent.monthlyReportId?.dailyPerformances && firstStudent.monthlyReportId.dailyPerformances.length > 0) {
          const firstDay = firstStudent.monthlyReportId.dailyPerformances[0];
        }
      }

      // Log numOfTotalAns from backend for each student and day
      reports?.forEach((report, idx) => {
        console.log(`[CLASS REPORT - API RESPONSE] 🎓 Report ${idx + 1} - Academic Year: ${report.academicYear}`);
        report.monthlyReports?.forEach((monthlyReport) => {
          const studentId = monthlyReport.monthlyReportId?.studentId;
          const month = monthlyReport.monthlyReportId?.month;
          console.log(`  👤 Student: ${studentId}, Month: ${month}`);
          monthlyReport.monthlyReportId?.dailyPerformances?.forEach((dailyPerf) => {
            console.log(`    📆 Day ${dailyPerf.day}: numOfTotalAns = ${dailyPerf.performanceId?.numOfTotalAns || 0}`);
          });
        });
      });

      const endTime = performance.now();
      console.log(`⚡ Class report fetch took ${endTime - startTime}ms`);

      // Cache the fetched data with timestamp
      const cacheEntry = {data: reports, timestamp: Date.now()};
      store.dispatch(storeClassReport(cacheKey, cacheEntry));

      return reports;
    } catch (error) {
      console.error('❌ Error in getReportsBySchoolAndClass:', error);
      throw error;
    }
  }

/**
   * Get class monthly report by classId and month
   * Caches all months EXCEPT the current month (always fetched fresh)
   * @param classId - Class ID
   * @param month - Month in YYYY-MM format
   * @returns ClassMonthlyReport or null
   */
  async getClassMonthlyReportByClassIdAndMonth(
    classId: string,
    month: string,
  ): Promise<ClassMonthlyReport | null> {
    const cacheKey = `${classId}-${month}`;
    const currentMonth = moment().format('YYYY-MM');

    // NEVER use cache for current month - always fetch fresh

      // Check for cached report in Redux state
      const state = store.getState();
      const cached = state.fullReport?.classMonthlyReportsByMonth?.[cacheKey];

      if (cached && !currentMonth) {
        console.log(`✅ Returning cached class monthly report for: ${cacheKey}`);
        return cached;
      }
   

    // Fetch fresh data
    try {
      console.log(`🔄 Fetching fresh class monthly report for: ${cacheKey}`);
      const startTime = performance.now();

      const classReport = (await this.classMonthlyReportService.getClassMonthlyReportByClassIdAndMonth(
        classId,
        month,
      )) as any as ClassMonthlyReport | null;

      const endTime = performance.now();
      console.log(`⚡ Class monthly report fetch took ${endTime - startTime}ms`);

      // Cache ONLY if it's NOT the current month
      if (classReport) {
        console.log(`💾 Caching report for past month: ${cacheKey}`);
        store.dispatch(storeClassMonthlyReportByMonth(classId, month, classReport));
      }

      return classReport;
    } catch (error) {
      console.error('❌ Error in getClassMonthlyReportByClassIdAndMonth:', error);
      return null;
    }
  }

}